//third party imports
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { of, race, Subject, timer } from 'rxjs';
import { delay, filter, map, take, takeUntil } from 'rxjs/operators';

//local imports
import { ApplicationConfiguration } from '@core/configuration';
import { TenantClaimResolverService } from '@core/security';

@Component({
  selector: 'auth-redirect',
  templateUrl: './auth-redirect.component.html',
  styleUrls: ['./auth-redirect.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthRedirectComponent implements OnInit, OnDestroy {
  private destroying$ = new Subject<void>();
  public isTakingLongerThanExpected = signal(false);
  public hasAuthStateInHash = signal(false);
  
  // 5 seconds timeout before executing rescue sequence
  private readonly AUTH_TIMEOUT_MS = 5000;
  
  constructor(
      private authService: MsalService,
      private msalBroadcastService: MsalBroadcastService,
      private router: Router,
      private applicationConfig: ApplicationConfiguration,
      private tenantClaimResolver: TenantClaimResolverService,
  ) {}

  ngOnInit(): void {
      const parsedUrl = this.router.parseUrl(this.router.url || '/');
      this.hasAuthStateInHash.set((parsedUrl.fragment || '').includes('state=') || parsedUrl.queryParamMap.has('state'));

      // Warn the user if it's lagging but hasn't timed out yet
      timer(3000).pipe(takeUntil(this.destroying$)).subscribe(() => {
          this.isTakingLongerThanExpected.set(true);
      });

      // 1. Manually initiate the redirect handler MSAL otherwise does implicitly
      this.authService.handleRedirectObservable().subscribe({
          next: (result) => {
              if (result) {
                 this.authService.instance.setActiveAccount(result.account);
                 this.resolveTenantFromAccount(result.account);
                 console.debug('AuthRedirectComponent: Redirect successful');
                 // If you have Application Insights or similar telemetry, track event here
                 return;
              }

              console.debug('AuthRedirectComponent: Redirect returned no result, will use active account');
              // Some callback flows complete with no direct result payload; use active account.
              this.resolveTenantFromAccount(this.authService.instance.getActiveAccount());
          },
          error: (err) => {
              console.error('AuthRedirectComponent: Redirect failed', err);
              // The router won't move if this errors out natively, push them to safety
              this.router.navigate(['/login-failed']).catch((err) => {
                  console.error('AuthRedirectComponent: failed to navigate to /login-failed', err);
              });
          }
      });

      // 2. Setup the "Deadlock Escape Hatch"
      const interactionDone$ = this.msalBroadcastService.inProgress$
          .pipe(
              filter((status: InteractionStatus) => status === InteractionStatus.None),
              take(1),
              map(() => 'resolved')
          );
          
      // Timeout observable
      const timeout$ = of('timed_out').pipe(delay(this.AUTH_TIMEOUT_MS));

      // Let them race
      race(interactionDone$, timeout$)
          .pipe(takeUntil(this.destroying$))
          .subscribe(async (raceResult) => {
              if (raceResult === 'timed_out') {
                  await this.executeDeadlockRescue();
                  return;
              }

              this.resolveTenantFromAccount(this.authService.instance.getActiveAccount());
          });
  }

  private resolveTenantFromAccount(account: AccountInfo | null): void {
      const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
      if (!claims) {
          return;
      }

      const tenant = this.tenantClaimResolver.resolveTenantFromClaims(claims);
      if (!tenant) {
          //TODO:DIW:This should be an error, and we should go to login-failed
          console.warn('AuthRedirectComponent: No tenant claim found in account claims. This may indicate a misconfiguration or missing claim in the identity provider.');
          return;
      }

      console.debug(`AuthRedirectComponent: Resolved tenant from claims: ${tenant}`);
      this.applicationConfig.tenant = tenant;
  }

    private async executeDeadlockRescue(): Promise<void> {
      console.warn('AuthRedirectComponent: MSAL Redirect processing timed out. Executing rescue route.');
      
      try {
          // Aggressive measure: Unstick the MSAL cache if stuck in 'interaction_in_progress'
          // This prevents MSAL guards on the root level from instantly re-throwing Errors
          this.authService.instance.clearCache();
      } catch (err) {
          console.error('AuthRedirectComponent: Failed to clear MSAL cache during rescue protocol', err);
      }
      
      // Bounce the user to the dedicated auth failure route.
      try {
          await this.router.navigate(['/login-failed']);
      } catch (err) {
          console.error('AuthRedirectComponent: failed to navigate to /login-failed during rescue route', err);
      }
  }

  ngOnDestroy(): void {
      this.destroying$.next(undefined);
      this.destroying$.complete();
  }
}
